// Alta de tienda y de zona en `retail-org`: formularios y bodies de los endpoints de alta.
import { z } from 'zod';
import type { CreateStoreRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateStoreRequest';
import type { CreateZoneRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateZoneRequest';
import { ZoneStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/ZoneStatusEnum';
import type { Zone } from '@/api/services/retail-org/retail-org.client';
import { mxPhoneField, mxZipCodeField } from '@/lib/formValidators';
import { toPhoneDigits } from '@/lib/phoneMx';

/** El backend acota la clave interna de la tienda a 10 caracteres. */
export const STORE_CODE_MAX = 10;

const required = (message: string) => z.string().trim().min(1, message);

/** Coordenada en texto: número dentro del rango que valida `retail-org`. */
const coordinate = (label: string, limit: number) =>
    required(`Escribe la ${label}.`).refine(
        (value) => {
            const n = Number(value);
            return Number.isFinite(n) && Math.abs(n) <= limit;
        },
        { message: `La ${label} va de -${String(limit)} a ${String(limit)}.` },
    );

export const newStoreSchema = z.object({
    name: required('Escribe el nombre.'),
    code: required('Escribe la clave.').max(
        STORE_CODE_MAX,
        `Máximo ${String(STORE_CODE_MAX)} caracteres.`,
    ),
    zoneId: z.string(),
    phone: mxPhoneField(),
    address1: required('Escribe la calle y número.'),
    neighborhood: z.string(),
    city: required('Escribe la ciudad.'),
    state: required('Escribe el estado.'),
    zipCode: mxZipCodeField(),
    lat: coordinate('latitud', 90),
    lng: coordinate('longitud', 180),
});

export type NewStoreForm = z.infer<typeof newStoreSchema>;

export const EMPTY_STORE_FORM: NewStoreForm = {
    name: '',
    code: '',
    zoneId: '',
    phone: '',
    address1: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    lat: '',
    lng: '',
};

export function toCreateStoreRequest(form: NewStoreForm): CreateStoreRequest {
    const neighborhood = form.neighborhood.trim();
    return {
        zoneId: form.zoneId === '' ? null : form.zoneId,
        code: form.code.trim(),
        name: form.name.trim(),
        address: {
            address1: form.address1.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            zipCode: form.zipCode.trim(),
            ...(neighborhood === '' ? {} : { neighborhood }),
        },
        location: { lat: Number(form.lat), lng: Number(form.lng) },
        phone: toPhoneDigits(form.phone),
        managerRetailUserId: null,
        schedule: null,
        dailyAvgCollectionsCount: null,
    };
}

/** Zonas activas para el select, en orden alfabético; la primera opción deja la tienda sin zona. */
export function zoneSelectOptions(zones: readonly Zone[]): { value: string; label: string }[] {
    const active = zones
        .filter((zone) => zone.status === ZoneStatusEnum.ACTIVE)
        .map((zone) => ({ value: zone.zoneId, label: zone.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es-MX'));
    return [{ value: '', label: 'Sin zona' }, ...active];
}

export const newZoneSchema = z.object({
    name: required('Escribe el nombre.'),
    region: required('Escribe la región.'),
});

export type NewZoneForm = z.infer<typeof newZoneSchema>;

export const EMPTY_ZONE_FORM: NewZoneForm = { name: '', region: '' };

export function toCreateZoneRequest(form: NewZoneForm): CreateZoneRequest {
    return { name: form.name.trim(), region: form.region.trim() };
}
